/** Die Kacheln der Finanzen (BE-05) - im Überblick des Bereichs und teils in den Tabs. */
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

import { Erkenntnis, Kennzahl, KennzahlZeile, StatKarte } from '@/ui/composites/Statistik';
import { Saeulen } from '@/ui/composites/Saeulen';
import { aboMonatlich, euro } from '@/domain/finanzen/finanzen';
import { useFinanzen } from '@/modules/finanzen/useFinanzen';
import { formatDate, formatDe } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';

const prozent = (x: number) => `${Math.round(x * 100)} %`;

/** Die vier Kennzahlen als Zeile - oben auf der Seite des Bereichs (D-00c). */
export function FinanzKennzahlenZeile({ spalten = 4 }: { spalten?: 2 | 3 | 4 }) {
  const f = useFinanzen();
  const s = f.summe;
  return (
    <KennzahlZeile spalten={spalten}>
        <Kennzahl label="Guthaben" wert={euro(f.guthaben)} hinweis="alle Konten" href="/finanzen/buchungen" />
        <Kennzahl label="Einnahmen" wert={euro(s.einnahmen)} hinweis="diesen Monat" href="/finanzen/buchungen" />
        <Kennzahl label="Ausgaben" wert={euro(s.ausgaben)} hinweis={f.tagesbudget !== null ? `noch ${euro(f.tagesbudget)} pro Tag im Budget` : 'diesen Monat'} href="/finanzen/budget" />
        <Kennzahl label="Sparquote" wert={s.sparquote === null ? '–' : prozent(s.sparquote)} hinweis={s.sparquote === null ? 'noch keine Einnahmen' : `${euro(Math.max(0, s.saldo))} übrig`} />
      </KennzahlZeile>
  );
}

export function FinanzKennzahlenKachel() {
  return (
    <StatKarte titel="Kennzahlen" frage="Wie steht mein Geld in diesem Monat?">
      <FinanzKennzahlenZeile spalten={2} />
    </StatKarte>
  );
}

export function FinanzBudgetKachel() {
  const { stand, tagesbudget } = useFinanzen();
  const mitBudget = stand.filter((z) => z.budget > 0);
  const drohend = mitBudget.filter((z) => z.droht).length;
  return (
    <StatKarte titel="Budget nach Kategorie" frage="Halte ich mein Budget? Der Strich zeigt die Hochrechnung bis Monatsende."
      aktion={<Link to="/finanzen/budget" className="text-[12px] text-accent-text hover:underline">Budget</Link>}>
      {stand.length === 0 ? <p className="text-sm text-ink-faint">Noch kein Budget und keine Ausgaben in diesem Monat.</p> : (
        <ul className="flex flex-col gap-2.5">
          {stand.map((z) => {
            const skala = Math.max(z.budget, z.ausgegeben, z.hochrechnung, 1);
            return (
              <li key={z.envelope?.id ?? 'ohne'} className="flex flex-col gap-0.5 text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate">{z.name}</span>
                  <span className="tabular-nums">{euro(z.ausgegeben)}{z.budget > 0 && <span className="text-ink-faint"> von {euro(z.budget)}</span>}</span>
                </div>
                {z.budget > 0 && (
                  <div className="relative h-1.5 rounded-full bg-surface-raised" role="img" aria-label={`${z.name}: ${euro(z.ausgegeben)} von ${euro(z.budget)}, Hochrechnung ${euro(z.hochrechnung)}`}>
                    <div className={cn('h-full rounded-full', z.ueberBudget ? 'bg-danger' : 'bg-accent')} style={{ width: `${Math.min(100, (z.ausgegeben / skala) * 100)}%` }} />
                    <div className="absolute -top-0.5 h-2.5 w-0.5 bg-ink" style={{ left: `${Math.min(100, (z.hochrechnung / skala) * 100)}%` }} title="Hochrechnung" />
                  </div>
                )}
                <span className={cn('text-[11px]', z.ueberBudget ? 'text-danger' : z.droht ? 'text-warn' : 'text-ink-faint')}>
                  {z.budget === 0 ? 'ohne Budget' : z.ueberBudget ? `${euro(-z.rest)} über dem Budget` : z.droht ? `Hochrechnung ${euro(z.hochrechnung)} – würde das Budget sprengen` : `noch ${euro(z.rest)}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Erkenntnis>{drohend ? `${drohend} ${drohend === 1 ? 'Kategorie droht' : 'Kategorien drohen'}, das Budget zu überschreiten.` : tagesbudget !== null ? `Bis Monatsende bleiben ${euro(tagesbudget)} pro Tag.` : null}</Erkenntnis>
    </StatKarte>
  );
}

export function FinanzVerlaufKachel() {
  const { verlauf } = useFinanzen();
  const punkte = (f: (m: (typeof verlauf)[number]) => number) => verlauf.map((m) => ({ key: m.monat, titel: formatDe(`${m.monat}-01`, 'MMMM yyyy'), wert: f(m) }));
  const links = formatDe(`${verlauf[0]!.monat}-01`, 'MMM yy');
  return (
    <StatKarte titel="Einnahmen und Ausgaben" frage="Wie entwickeln sich Einnahmen und Ausgaben in den letzten 6 Monaten?">
      <Saeulen label="Einnahmen je Monat" punkte={punkte((m) => m.einnahmen)} einheit="Einnahmen" links={links} rechts="dieser Monat" format={euro} />
      <Saeulen label="Ausgaben je Monat" punkte={punkte((m) => m.ausgaben)} einheit="Ausgaben" links={links} rechts="dieser Monat" format={euro} />
    </StatKarte>
  );
}

export function FinanzAbosKachel() {
  const { abosSortiert, abos } = useFinanzen();
  const monat = abos.reduce((s, a) => s + aboMonatlich(a), 0);
  return (
    <StatKarte titel="Abos und Fristen" frage="Was wird wann abgebucht, und wo läuft eine Kündigungsfrist ab?"
      aktion={<Link to="/finanzen/abos" className="text-[12px] text-accent-text hover:underline">Alle Abos</Link>}>
      {abosSortiert.length === 0 ? <p className="text-sm text-ink-faint">Noch keine Abos angelegt.</p> : (
        <ul className="flex flex-col divide-y divide-line">
          {abosSortiert.slice(0, 5).map((z) => (
            <li key={z.abo.id} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="min-w-0 flex-1 truncate">{z.abo.name}</span>
              {z.dringend && <span className="flex items-center gap-1 text-[11px] text-warn"><AlertTriangle className="size-3.5" aria-hidden /> kündigen bis {formatDate(z.kuendigenBis!)}</span>}
              <span className="tabular-nums text-ink-muted">{euro(z.abo.amountCents)}</span>
              <span className="w-14 text-right text-[11px] tabular-nums text-ink-faint">{formatDe(z.abo.nextDue, 'dd.MM.')}</span>
            </li>
          ))}
        </ul>
      )}
      {abos.length > 0 && <Erkenntnis>{`Abos kosten zusammen ${euro(monat)} im Monat (${euro(monat * 12)} im Jahr).`}</Erkenntnis>}
    </StatKarte>
  );
}

export function FinanzBuchungenKachel() {
  const { tx, kategorien } = useFinanzen();
  const name = new Map(kategorien.map((k) => [k.id, k.name]));
  const letzte = [...tx].reverse().slice(0, 6);
  return (
    <StatKarte titel="Letzte Buchungen" aktion={<Link to="/finanzen/buchungen" className="text-[12px] text-accent-text hover:underline">Alle Buchungen</Link>}>
      {letzte.length === 0 ? <p className="text-sm text-ink-faint">Noch keine Buchungen.</p> : (
        <ul className="flex flex-col divide-y divide-line">
          {letzte.map((t) => (
            <li key={t.id} className="flex items-center gap-2 py-1.5 text-sm">
              {t.amountCents < 0 ? <ArrowUpRight className="size-4 shrink-0 text-ink-muted" aria-label="Ausgabe" /> : <ArrowDownLeft className="size-4 shrink-0 text-ok" aria-label="Einnahme" />}
              <span className="min-w-0 flex-1 truncate">{t.payee ?? (t.envelopeId ? name.get(t.envelopeId) : undefined) ?? (t.kind === 'einnahme' ? 'Einnahme' : 'Ausgabe')}</span>
              <span className={cn('tabular-nums', t.amountCents > 0 && 'text-ok')}>{euro(t.amountCents)}</span>
              <span className="w-14 text-right text-[11px] tabular-nums text-ink-faint">{formatDe(t.date, 'dd.MM.')}</span>
            </li>
          ))}
        </ul>
      )}
    </StatKarte>
  );
}
